// src/logger/logger.ts
import { DrizzleError } from "drizzle-orm";
import { DateTime } from "luxon";
import { fromError } from "zod-validation-error";

// src/logger/pc.ts
var replaceClose = (string, close, replace, index) => {
  const start = string.substring(0, index) + replace;
  const end = string.substring(index + close.length);
  const nextIndex = end.indexOf(close);
  return ~nextIndex !== 0 ? start + replaceClose(end, close, replace, nextIndex) : start + end;
};
var formatter = (open, close, replace = open) => (input) => {
  const string = `${input}`;
  const index = string.indexOf(close, open.length);
  return ~index !== 0 ? open + replaceClose(string, close, replace, index) + close : open + string + close;
};
var createColors = (enabled = true) => ({
  isColorSupported: enabled,
  reset: enabled ? (s) => `\x1B[0m${s}\x1B[0m` : String,
  bold: enabled ? formatter("\x1B[1m", "\x1B[22m", "\x1B[22m\x1B[1m") : String,
  dim: enabled ? formatter("\x1B[2m", "\x1B[22m", "\x1B[22m\x1B[2m") : String,
  italic: enabled ? formatter("\x1B[3m", "\x1B[23m") : String,
  underline: enabled ? formatter("\x1B[4m", "\x1B[24m") : String,
  inverse: enabled ? formatter("\x1B[7m", "\x1B[27m") : String,
  hidden: enabled ? formatter("\x1B[8m", "\x1B[28m") : String,
  strikethrough: enabled ? formatter("\x1B[9m", "\x1B[29m") : String,
  black: enabled ? formatter("\x1B[30m", "\x1B[39m") : String,
  red: enabled ? formatter("\x1B[31m", "\x1B[39m") : String,
  green: enabled ? formatter("\x1B[32m", "\x1B[39m") : String,
  yellow: enabled ? formatter("\x1B[33m", "\x1B[39m") : String,
  blue: enabled ? formatter("\x1B[34m", "\x1B[39m") : String,
  magenta: enabled ? formatter("\x1B[35m", "\x1B[39m") : String,
  cyan: enabled ? formatter("\x1B[36m", "\x1B[39m") : String,
  white: enabled ? formatter("\x1B[37m", "\x1B[39m") : String,
  gray: enabled ? formatter("\x1B[90m", "\x1B[39m") : String,
  bgBlack: enabled ? formatter("\x1B[40m", "\x1B[49m") : String,
  bgRed: enabled ? formatter("\x1B[41m", "\x1B[49m") : String,
  bgGreen: enabled ? formatter("\x1B[42m", "\x1B[49m") : String,
  bgYellow: enabled ? formatter("\x1B[43m", "\x1B[49m") : String,
  bgBlue: enabled ? formatter("\x1B[44m", "\x1B[49m") : String,
  bgMagenta: enabled ? formatter("\x1B[45m", "\x1B[49m") : String,
  bgCyan: enabled ? formatter("\x1B[46m", "\x1B[49m") : String,
  bgWhite: enabled ? formatter("\x1B[47m", "\x1B[49m") : String,
  orange: enabled ? formatter("\x1B[38;5;208m", "\x1B[39m") : String,
  bgOrange: enabled ? formatter("\x1B[48;5;208m", "\x1B[49m") : String
});
var colors = createColors(process.env.NODE_ENV !== "production");

// src/logger/logger.ts
function safeStringify(obj, depth = 1) {
  try {
    if (obj === null || obj === void 0) {
      return String(obj);
    }
    if (typeof obj !== "object") {
      return String(obj);
    }
    if (depth <= 0) {
      return "[Object]";
    }
    if (Array.isArray(obj)) {
      return `[${obj.map((item) => safeStringify(item, depth - 1)).join(", ")}]`;
    }
    const props = Object.getOwnPropertyNames(obj).slice(0, 50);
    const result = props.map((key) => {
      try {
        const value = obj[key];
        return `${key}: ${safeStringify(value, depth - 1)}`;
      } catch (e) {
        return `${key}: [Error]`;
      }
    }).join(", ");
    return `{${result}}`;
  } catch (error) {
    return "[Error during stringify]";
  }
}
function isPostgresError(error) {
  return error instanceof Error && "code" in error && typeof error.code === "string" && // Common Postgres error codes start with a letter followed by numbers
  (/^[A-Z][0-9]{4}$/.test(error.code) || // Or it has typical Postgres properties
  "schema" in error || "table" in error || "column" in error || "constraint" in error || "detail" in error);
}
var Logger = class _Logger {
  // Easily change this number
  constructor(context) {
    this.context = context;
    this.context = context;
  }
  static STACK_TRACE_LENGTH = 3;
  static serializeMessage(message) {
    if (typeof message === "object" && message !== null) {
      if (isPostgresError(message)) {
        const pgError = message;
        return {
          type: "PostgresError",
          name: pgError.name,
          message: pgError.message,
          stack: this.formatStack(pgError.stack),
          details: {
            code: pgError.code,
            detail: pgError.detail,
            schema: pgError.schema,
            table: pgError.table,
            column: pgError.column,
            dataType: pgError.dataType,
            constraint: pgError.constraint
          }
        };
      }
      if (message instanceof DrizzleError) {
        const details = {};
        const stack = this.formatStack(message.stack);
        let relationInfo = null;
        if (stack && stack.includes("normalizeRelation")) {
          const stackLines = message.stack?.split("\n") || [];
          relationInfo = {
            fullStack: stackLines.filter(
              (line) => line.includes("normalizeRelation") || line.includes("buildRelationalQuery") || line.includes("findMany") || line.includes("findFirst")
            ).map((line) => line.trim()),
            // Try to extract table names from the error message and stack
            possibleTables: stackLines.map((line) => {
              const tableMatch = line.match(/table[s]?\s*['"]([^'"]+)['"]/i);
              return tableMatch ? tableMatch[1] : null;
            }).filter(Boolean)
          };
        }
        for (const [key, value] of Object.entries(message)) {
          if (!["name", "message", "stack"].includes(key)) {
            details[key] = value;
          }
        }
        return {
          type: "DrizzleError",
          name: message.name,
          message: message.message,
          stack: this.formatStack(message.stack),
          details: {
            ...Object.keys(details).length > 0 ? details : {},
            ...relationInfo ? { relationInfo } : {},
            errorLocation: message.stack?.split("\n")[1]?.trim(),
            // Get the immediate error location
            query: message.query || void 0
          }
        };
      }
      if (message instanceof Error && "issues" in message) {
        try {
          const zodError = fromError(message);
          return {
            type: "ZodError with Issues",
            name: message.name,
            message: zodError.message,
            details: zodError.details[0]?.path ? {
              path: zodError.details[0]?.path?.join(".") || "",
              message: zodError.details[0]?.message || ""
            } : null
          };
        } catch (e) {
          return {
            type: "NON-ZodError with Issues",
            name: message.name,
            message: message.message,
            error: "Failed to parse Zod error"
          };
        }
      }
      if (message instanceof Error) {
        const supabaseProps = ["statusCode", "status", "code", "details", "hint", "requestId"];
        const details = {};
        for (const [key, value] of Object.entries(message)) {
          if (supabaseProps.includes(key)) {
            details[key] = value;
          }
        }
        return {
          type: "Error",
          name: message.name,
          message: message.message,
          stack: this.formatStack(message.stack),
          ...Object.keys(details).length > 0 ? { details } : {},
          ...message
          // Include any remaining custom properties
        };
      }
      return safeStringify(message);
    }
    return message;
  }
  static logMessage(type, context, messages, colorFn) {
    const prefix = colorFn(
      `[${type.toUpperCase()} - ${DateTime.now().toLocaleString(DateTime.DATETIME_FULL)} - ${colors.yellow(context)}]:`
    );
    const serializedMessages = messages.map((msg) => this.serializeMessage(msg));
    console.log(prefix, ...serializedMessages);
  }
  log(...messages) {
    _Logger.logMessage("log", this.context, messages, colors.green);
  }
  debug(...messages) {
    _Logger.logMessage("debug", this.context, _Logger.processMessages(messages), colors.orange);
  }
  error(...messages) {
    _Logger.logMessage("error", this.context, messages, colors.red);
  }
  info(...messages) {
    _Logger.logMessage("info", this.context, messages, colors.cyan);
  }
  warn(...messages) {
    _Logger.logMessage("warn", this.context, messages, colors.yellow);
  }
  static log(context, ...messages) {
    this.logMessage("log", context, messages, colors.green);
  }
  static debug(context, ...messages) {
    this.logMessage("debug", context, this.processMessages(messages), colors.orange);
  }
  static error(context, ...messages) {
    this.logMessage("error", context, messages, colors.red);
  }
  static info(context, ...messages) {
    this.logMessage("info", context, messages, colors.cyan);
  }
  static warn(context, ...messages) {
    this.logMessage("warn", context, messages, colors.yellow);
  }
  static formatStack(stack) {
    if (!stack) return void 0;
    const lines = stack.split("\n");
    return lines.slice(0, _Logger.STACK_TRACE_LENGTH).map((line) => line?.trim()).join("\n");
  }
  static processMessages(messages) {
    return messages.map((msg) => {
      if (typeof msg === "object" && msg !== null) {
        const obj = msg;
        if ("stack" in obj) {
          return { ...obj, stack: this.formatStack(obj.stack) };
        }
      }
      return msg;
    });
  }
};

// src/colors/random-color-generator.ts
var basePalette = [
  "#FF8B3D",
  // JPM
  "#5B9BD5",
  // Mastercard
  "#8064A2",
  // Capital One
  "#9B9B9B",
  // Equifax
  "#262626",
  // Vantage Score
  "#C9C9C9",
  // Nerd Wallet
  "#8B6B43",
  // American Express
  "#D9D9D9",
  // 1st Security
  "#3498DB",
  // Bright Blue
  "#E74C3C",
  // Bright Red
  "#2ECC71",
  // Emerald Green
  "#F39C12",
  // Orange
  "#9B59B6",
  // Amethyst Purple
  "#1ABC9C",
  // Turquoise
  "#D35400",
  // Pumpkin Orange
  "#34495E",
  // Dark Blue Gray
  "#16A085",
  // Green Sea
  "#C0392B",
  // Dark Red
  "#8E44AD",
  // Wisteria Purple
  "#27AE60",
  // Nephritis Green
  "#E67E22",
  // Carrot Orange
  "#2980B9",
  // Belize Hole Blue
  "#F1C40F",
  // Sunflower Yellow
  "#7F8C8D",
  // Asbestos Gray
  "#D35400",
  // Pumpkin Orange
  "#2C3E50",
  // Midnight Blue
  "#F39C12",
  // Orange
  "#16A085"
  // Green Sea
];
var ColorManager = class {
  availableColors;
  _usedColors;
  assignments;
  researchProjectId;
  constructor(researchProjectId) {
    this.researchProjectId = researchProjectId;
    this.assignments = [];
    this.availableColors = [];
    this._usedColors = /* @__PURE__ */ new Set();
    this.generateColors();
  }
  get usedColors() {
    return this._usedColors;
  }
  set usedColors(colors2) {
    this._usedColors = new Set(colors2);
  }
  generateColors() {
    this.availableColors = basePalette;
  }
  getNextColor() {
    const unusedColor = this.availableColors.find((color) => !this.usedColors.has(color));
    if (unusedColor) {
      this.usedColors.add(unusedColor);
      return unusedColor;
    }
    this.usedColors.clear();
    return this.getNextColor();
  }
  /**
   * Automatically assign a color to an entity alias
   */
  assignColor(entityAliasId) {
    const existing = this.assignments.find((a) => a.entityAliasId === entityAliasId);
    if (existing) {
      return existing.color;
    }
    const color = this.availableColors[0];
    if (!color) {
      throw new Error("No more colors available for assignment");
    }
    this.availableColors.splice(0, 1);
    this.assignments.push({ entityAliasId, color });
    return color;
  }
  /**
   * Manually set a color for an entity alias
   * Returns false if color is already in use by another entity
   */
  setColor(entityAliasId, color) {
    const existingAssignment = this.assignments.find((a) => a.color === color && a.entityAliasId !== entityAliasId);
    if (existingAssignment) {
      return false;
    }
    const index = this.assignments.findIndex((a) => a.entityAliasId === entityAliasId);
    if (index >= 0) {
      const assignment = this.assignments[index];
      if (assignment) {
        const oldColor = assignment.color;
        if (oldColor && !this.availableColors.includes(oldColor)) {
          this.availableColors.push(oldColor);
        }
        assignment.color = color;
      }
    } else {
      this.assignments.push({ entityAliasId, color });
    }
    const colorIndex = this.availableColors.indexOf(color);
    if (colorIndex >= 0) {
      this.availableColors.splice(colorIndex, 1);
    }
    return true;
  }
  /**
   * Get all current color assignments
   */
  getAssignments() {
    return [...this.assignments];
  }
  /**
   * Get available colors that can be manually assigned
   */
  getAvailableColors() {
    if (this.availableColors.length === 0) {
      this.generateColors();
    }
    return [...this.availableColors];
  }
  regenerateColors() {
    this.generateColors();
    return [...this.availableColors];
  }
  /**
   * Release a color assignment, making it available again
   */
  releaseColor(entityAliasId) {
    const index = this.assignments.findIndex((a) => a.entityAliasId === entityAliasId);
    if (index >= 0) {
      const assignment = this.assignments[index];
      if (assignment) {
        const color = assignment.color;
        this.assignments.splice(index, 1);
        this.availableColors.push(color);
      }
    }
  }
};
export {
  ColorManager,
  Logger,
  basePalette
};
